import type { ReactNode } from 'react'
import { MessageSquare } from 'lucide-react'
import type { AIAgent } from '@/data/industries'

const TONES = [
  { bg: 'var(--color-landing-accent)', fg: '#fff' },
  { bg: '#ff00f2', fg: '#0a0a0a' },
  { bg: 'var(--color-landing-secondary)', fg: '#fff' },
  { bg: '#0a0a0a', fg: '#fff' },
] as const

const dots = (alpha: number): React.CSSProperties => ({
  backgroundImage: `radial-gradient(rgba(255,255,255,${alpha}) 0.9px, transparent 1.3px)`,
  backgroundSize: '6px 6px',
})

function AgentCard({ agent, index }: { agent: AIAgent; index: number }) {
  const tone = TONES[index % TONES.length]!
  const Icon = agent.icon
  return (
    <div className="relative flex h-full w-full flex-col justify-between overflow-hidden p-3 md:p-4" style={{ backgroundColor: tone.bg, color: tone.fg }}>
      <span className="absolute inset-0" style={dots(0.28)} />
      <span className="relative flex h-9 w-9 items-center justify-center bg-white text-[#0a0a0a]">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <span className="relative">
        <span className="block font-[family-name:var(--font-landing-display)] text-[1.05rem] uppercase leading-[1.3] md:text-[1.2rem]">{agent.name}</span>
        <span className="mt-1 block text-[0.65rem] font-bold uppercase leading-tight tracking-[0.06em] opacity-85">{agent.role}</span>
      </span>
    </div>
  )
}

function AgentSays({ agent, index }: { agent: AIAgent; index: number }) {
  const tone = TONES[(index + 1) % TONES.length]!
  return (
    <div className="flex h-full w-full flex-col gap-3 border border-[var(--color-landing-gray-200)] bg-white p-3 text-[#0a0a0a] dark:border-[var(--color-landing-gray-700)] dark:bg-[#16161f] dark:text-white md:p-4">
      <span className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-[family-name:var(--font-landing-display)] text-[0.7rem]" style={{ backgroundColor: tone.bg, color: tone.fg }}>
          {agent.name[0]}
        </span>
        <span className="text-[0.65rem] font-bold uppercase tracking-[0.06em]">{agent.name}</span>
      </span>
      <span className="relative bg-[var(--color-landing-gray-100)] p-2.5 text-[0.75rem] font-semibold leading-snug dark:bg-[#22222c]">
        <MessageSquare aria-hidden className="mb-1.5 h-3.5 w-3.5 text-[var(--color-landing-accent)]" />
        {agent.catchphrase}
      </span>
      <span className="mt-auto flex flex-col gap-1.5">
        {[100, 82, 64].map((w) => (
          <span key={w} className="block h-[3px] bg-[var(--color-landing-gray-200)] dark:bg-[var(--color-landing-gray-700)]" style={{ width: `${w}%` }} />
        ))}
      </span>
    </div>
  )
}

/** Dock-marquee tiles for an industry's agents: each agent's card, then a tile of it at work. */
export function agentTiles(agents: AIAgent[]): ReactNode[] {
  return agents.flatMap((agent, i) => [
    <AgentCard key={`card-${agent.name}`} agent={agent} index={i} />,
    <AgentSays key={`says-${agent.name}`} agent={agent} index={i} />,
  ])
}
